document.addEventListener('alpine:init', () => {
    Alpine.data('handleVariant', (pProduct = null, optionsByName = null, selectedOrFirstAvailable = null) => ({
        /**
         * The current product object.
         */
        selectedVariant : selectedOrFirstAvailable?.id,
        product: {
            ...pProduct,
            options_by_name: optionsByName,
            initial_selected_or_first_available_variant: selectedOrFirstAvailable,
            selected_or_first_available_variant: {
                ...selectedOrFirstAvailable,
                featured_image: selectedOrFirstAvailable?.featured_image?.src,
                quantity: 1,
                addToCartButton: true,
                _price: selectedOrFirstAvailable?.price ?? 0,
                set price(value) {
                    if (isNaN(value)) {
                        throw new Error('Liquify - Price must be a number');
                    }
                    this._price = value;
                },
                get price() {
                    return this._price;
                },
            },

        },

        hasReachedPreOrderLimit() {
            try {
                const remainingCapacity = this.getRemainingPreOrderCapacity();
                const hasReachedLimit = remainingCapacity <= 0;
                
                // console.log(`Pre-order limit check: Remaining capacity=${remainingCapacity}, Reached limit=${hasReachedLimit}`);
                
                return hasReachedLimit;
            } catch (error) {
                console.error('Error checking pre-order limit:', error);
                return false;
            }
        },

        /**
         * helper properties
         */
        firstOptionHasAvailableChildren: {},
        selectorType: 'radio',
        formSelector: "#product_form_" + (pProduct?.id ? pProduct.id : selectedOrFirstAvailable?.id),
        isProductPage: window.location.pathname.includes('/products/'),
        filter: {},

        // trackVariantChanges() {
        //     // Create a MutationObserver to monitor variant selection
        //     const variantContainer = this.$el;
        //     const observer = new MutationObserver((mutations) => {
        //         mutations.forEach((mutation) => {
        //             if (mutation.type === 'attributes' && 
        //                 (mutation.target.tagName === 'INPUT' || mutation.target.tagName === 'SELECT') &&
        //                 (mutation.attributeName === 'checked' || mutation.attributeName === 'selected')) {
                        
        //                 console.log('Variant selection changed:', mutation.target);
        //                 this.logPreOrderStatus();
        //             }
        //         });
        //     });
            
        //     // Start observing all variant options
        //     const options = variantContainer.querySelectorAll('input[type="radio"], select');
        //     options.forEach(option => {
        //         observer.observe(option, { attributes: true });
        //     });
            
        //     return observer; // Store this to disconnect later if needed
        // },

        trackVariantChanges() {
            // Add a flag to prevent recursive updates
            if (this._isUpdating || this._variantObserver) return null;
            
            const variantContainer = this.$el;
            if (!variantContainer) return null;
            
            this._variantObserver = new MutationObserver((mutations) => {
                // Prevent recursive updates
                if (this._isUpdating) return;
                
                this._isUpdating = true;
                
                // Use requestAnimationFrame for better performance instead of setTimeout
                requestAnimationFrame(() => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'attributes' && 
                            (mutation.target.tagName === 'INPUT' || mutation.target.tagName === 'SELECT') &&
                            (mutation.attributeName === 'checked' || mutation.attributeName === 'selected')) {
                            
                            // console.log('Variant selection changed:', mutation.target);
                            this.logPreOrderStatus();
                        }
                    });
                    // Reset the flag after processing
                    this._isUpdating = false;
                });
            });
            
            // Start observing - cache the options query
            const options = variantContainer.querySelectorAll('input[type="radio"], select');
            options.forEach(option => {
                this._variantObserver.observe(option, { attributes: true });
            });
            
            return this._variantObserver;
        },

        init() {
        // Track variant changes
        this.variantObserver = this.trackVariantChanges();
        
        // Store a unique identifier for this instance
        this.instanceId = Math.random().toString(36).substring(2, 9);
        
        // Flag to prevent recursive updates
        this._isProcessingCartUpdate = false;
        
        // Define handler with safeguards to prevent loops
        this.cartUpdateHandler = (event) => {
            // Skip if we're already processing an update
            if (this._isProcessingCartUpdate) {
                // console.log('Skipping cart update - already processing');
                return;
            }
            
            // Skip if this is an event we triggered ourselves
            if (event.detail && event.detail.source === this.instanceId) {
                // console.log('Skipping self-triggered cart update');
                return;
            }
            
            // console.log('Processing cart update event');
            
            // Set flag to prevent recursive updates
            this._isProcessingCartUpdate = true;
            
            // Clear any existing timer
            if (this._cartUpdateTimer) {
                clearTimeout(this._cartUpdateTimer);
            }
            
            // Use setTimeout to break the event loop
            this._cartUpdateTimer = setTimeout(async () => {
                try {
                    await this.fetchCart();
                    
                    // Important: wait until next tick to update UI
                    this.$nextTick(() => {
                        this.updateVariantStatuses();
                        
                        // Clear processing flag after a short delay
                        setTimeout(() => {
                            this._isProcessingCartUpdate = false;
                        }, 100);
                    });
                } catch (error) {
                    console.error('Error processing cart update:', error);
                    this._isProcessingCartUpdate = false;
                }
                
                delete this._cartUpdateTimer;
            }, 300); // Longer timeout to reduce update frequency
        };
        
        // Clean up old listeners and add new one
        document.removeEventListener('cart:updated', this.cartUpdateHandler);
        document.addEventListener('cart:updated', this.cartUpdateHandler);
        
        // Initial cart fetch - DO NOT update UI here directly
        // console.log('Initializing cart data...');
        this.fetchCart().then(() => {
            // console.log('Cart data initialized');
        });
    },

        // Add this method to trigger cart updates in a safe way
        triggerCartUpdated() {
            document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { source: this.instanceId }
            }));
        },

        destroy() {            
            // Remove event listeners
            document.removeEventListener('cart:updated', this.cartUpdateHandler);
            
            // Disconnect observers
            if (this.variantObserver) {
                this.variantObserver.disconnect();
                this.variantObserver = null;
            }
            
            if (this._variantObserver) {
                this._variantObserver.disconnect();
                this._variantObserver = null;
            }
            
            // Clear cached DOM elements to prevent memory leaks
            if (this._cachedForm) {
                this._cachedForm = null;
            }
            if (this._cachedInputWrappers) {
                this._cachedInputWrappers = null;
            }
            
            // Clear timers
            if (this._cartUpdateTimer) {
                clearTimeout(this._cartUpdateTimer);
                this._cartUpdateTimer = null;
            }
            
            if (this._changeThrottle) {
                clearTimeout(this._changeThrottle);
                this._changeThrottle = null;
            }
        },

        qs(selector) {
            return document.querySelector(selector)
        },

        cartItems: [],

        async fetchCart() {
            // console.log('Attempting to fetch cart...');
            try {
                const response = await fetch('/cart.js');
                if (!response.ok) {
                    console.error('Cart fetch response not OK:', response.status, response.statusText);
                    return [];
                }
                
                const cart = await response.json();
                // console.log('Cart fetch successful:', cart);
                this.cartItems = cart.items || [];
                
                // Log each cart item for clarity
                if (this.cartItems.length > 0) {
                    // console.group('Cart Items:');
                    this.cartItems.forEach(item => {
                        // console.log(`- Variant ID: ${item.variant_id}, Product: ${item.title}, Quantity: ${item.quantity}`);
                    });
                    // console.groupEnd();
                } else {
                    // console.log('Cart is empty');
                }
                
                return this.cartItems;
            } catch (error) {
                console.error('Error fetching cart:', error);
                return [];
            }
        },

        /**
         * @wip: returns an object with the available variants based on the current filter-selection.
         *   result.existingOptions contains the list of options based on the given filter.
         */
        get variantsBasedOnFilter() {
            let result = {
                availableVariants: [],
            };

            // If no filter is set, return all variants
            if (Object.keys(this.filter).length === 0) {
                result.availableVariants = this.product?.variants;
            } else {
                // Filter variants based on current filter
                result.availableVariants = this.product?.variants.filter(variant => {
                    // Define, how many filters should match
                    // let filter = this.getFirstTwoSelectedFilters()
                    let filter = this.filter

                    return Object.entries(filter).every(([filterName, filterValue]) => {
                        // console.log("filterValue", filterValue, this.getFirstTwoSelectedFilters())
                        const matchingOption = Object.entries(variant).find(([key, value]) =>
                            key.startsWith('option') && value === filterValue
                        );
                        return matchingOption !== undefined;
                    });
                });
            }

            result.existingOptions = this.getExistingOptions(result.availableVariants);

            // console.log("Liquify - return of variantsBasedOnFilter", result)

            return result;
        },

        getFirstTwoSelectedFilters() {
            return Object.entries(this.filter).slice(0, 2).reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});
        },

        getExistingOptions(variants) {
            let uniqueOptions = {
                option1: [...new Set(variants?.map(item => item.option1))],
                option2: [...new Set(variants?.map(item => item.option2))],
                option3: [...new Set(variants?.map(item => item.option3))],
            }

            let optionsByName = {}
            Object.assign(optionsByName, this.product.options_by_name);

            let optionNames = this.optionNames(optionsByName);

            let result = {}
            Object.keys(optionNames).forEach(optionName => {
                let optionValues = Object.values(optionNames[optionName]);
                // map the actual option name to the unique options
                Object.values(uniqueOptions).forEach(value => {
                    if (value.length > 0 && optionValues.includes(value[0])) {
                        result[optionName] = value;
                    }
                });
            });

            return result
        },

        optionNames(data) {
            const result = {};

            Object.keys(data).forEach(optionName => {
                result[optionName] = data[optionName].option.values;
            });

            return result;
        },

        observeVariants() {
            if(this.isProductPage) {
                // todo: maybe via $dispatch or helper?!
                let $this = this

                window.onpopstate = function(event) {
                    const urlParams = new URLSearchParams(window.location.search);
                    const variantId = urlParams.get('variant');
                    const observer = new MutationObserver(mutationCallback);

                    //fixme: maybe in helper?!
                    function mutationCallback(mutations) {
                        mutations.forEach(function(mutation) {
                            if (mutation.type === "attributes" && (mutation.attributeName === "checked" || mutation.attributeName === "selected")) {
                                // fixme: kann/muss theoretisch weg, da über :class gelöst werden muss
                                mutation.target.previousElementSibling.classList.remove('w--redirected-checked', 'w--redirected-focus')
                                if(mutation?.target?.checked || mutation?.target?.selected) {
                                    mutation.target.previousElementSibling.classList.add('w--redirected-checked', 'w--redirected-focus')
                                    $this.setVariant(false)
                                }
                            }
                        })
                        observer.disconnect()
                    }

                    selectedOrFirstAvailable = $this?.product.variants.filter(variant => parseInt(variant.id) === parseInt(variantId))[0]

                    $this.product.selected_or_first_available_variant = {...$this.product.selected_or_first_available_variant, ...selectedOrFirstAvailable};

                    $this.product.options.forEach(function (option) {
                        let selector = " [data-productVariantsLoop] input[name='" + option + "']"
                        if($this.selectorType === 'select') {
                            selector = " [data-productVariantsLoop] select[name='" + option + "'] option"
                        }
                        document.querySelectorAll($this.formSelector + selector).values().forEach(function(element) {
                            observer.observe(element, {
                                attributes: true
                            });
                        })
                    })
                }
            }
        },

        updateVariantStatuses() {
            // Guard against recursive updates
            if (this._isUpdatingVariantStatuses) {
                // console.log('Already updating variant statuses, skipping');
                return;
            }

            this._isUpdatingVariantStatuses = true;
            // console.log("Liquify - updateVariantStatuses");

            try {
                // Cache form element to avoid repeated queries
                let form = this._cachedForm || this.$el.closest('form') || this.$el.querySelector('form');
                if (!this._cachedForm) {
                    this._cachedForm = form;
                }
                
                const selectedOptionOneVariants = this.product.variants.filter((variant) => {
                        let element = form?.querySelector('[data-productVariantsLoop] input[type="radio"]:checked');
                        if (this.selectorType === 'select') {
                            element = form?.querySelector('[data-productVariantsLoop] select')
                        }
                        return element?.value === variant.option1
                    }
                ); 

                // Cache input wrappers to avoid repeated queries
                let inputWrappers = this._cachedInputWrappers;
                if (!inputWrappers && form?.querySelectorAll('[data-ProductVariantsLoop]')) {
                    inputWrappers = [...form.querySelectorAll('[data-ProductVariantsLoop]')];
                    this._cachedInputWrappers = inputWrappers;
                }

                inputWrappers?.forEach((option, index) => {
                    if (index === 0) return;

                    let optionInputs = [...option.querySelectorAll('input[type="radio"]')];

                    if (this.selectorType === 'select') {
                        optionInputs = [...option.querySelectorAll('option')];
                    }


                    let previousOptionSelected = inputWrappers[index - 1].querySelector(':checked')
                    if (previousOptionSelected?.getAttribute('data-disabled') !== null) {
                        previousOptionSelected = inputWrappers[index - 1].querySelector('input:not([data-disabled]), option:not([data-disabled])')
                    }
                    previousOptionSelected = previousOptionSelected.value
                    if (!previousOptionSelected) {
                        return;
                    }

                    const availableOptionInputsValue = selectedOptionOneVariants
                        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
                        .map((variantOption) => variantOption[`option${index + 1}`]);

                    this.setInputAvailability(optionInputs, availableOptionInputsValue);

                    // Select the next available option
                    if (this.selectorType === 'select') {
                        selectedVal = option.querySelector('select').value;
                        for (let i = 0; i < option.querySelector('select').options.length; i++) {
                            // Select the next available option if not in available options
                            if(availableOptionInputsValue.indexOf(selectedVal) === -1
                                && option.querySelector('select').options[i].text === availableOptionInputsValue[0]
                            ) {
                                option.querySelector('select').selectedIndex = i;
                                break;
                            }
                        }
                    }
                });

                this.updateAddToCartButton(form);
            } catch (error) {
                console.error('Error updating variant statuses:', error);
            } finally {
                // Clear flag after a delay
                setTimeout(() => {
                    this._isUpdatingVariantStatuses = false;
                }, 50);
            }
        },

        updateAddToCartButton(form) {
            try {
                // console.log('Liquify - updateAddToCartButton()');
                
                if (!form) {
                    console.warn('Liquify - No form provided to updateAddToCartButton');
                    return;
                }
                
                // Find button using various selectors
                let addToCartButton = null;
                
                const possibleButtons = [
                    form.closest('[li-element="product-variant-container"]')?.querySelector('[li-element="add-to-cart"]'),
                    this.$el.querySelector('.button.max-width-full.w-button'),
                    form.closest('.button.max-width-full.w-button'),
                    document.querySelector('.product-header_ad-to-cart-wrapper .button.max-width-full.w-button')
                ];
                
                for (const btn of possibleButtons) {
                    if (btn) {
                        addToCartButton = btn;
                        break;
                    }
                }
                
                if (!addToCartButton) {
                    // console.warn('Liquify - Add to cart button not found');
                    return;
                }
                
                // First check the basic availability
                const variant = this.product.selected_or_first_available_variant;
                if (!variant || !variant.available) {
                    // Not available - simple case
                    // console.log('Variant not available, disabling button');
                    addToCartButton.textContent = 'Sold Out';
                    addToCartButton.setAttribute('disabled', 'disabled'); // Set disabled attribute
                    addToCartButton.classList.add('disabled');
                    this.product.selected_or_first_available_variant.addToCartButton = false;
                    
                    // Double-check disabled state was applied
                    // console.log('Button disabled state:', {
                    //     hasAttribute: addToCartButton.hasAttribute('disabled'),
                    //     hasClass: addToCartButton.classList.contains('disabled')
                    // });
                    return;
                }
                
                // Then check pre-order state
                const hasReachedLimit = this.hasReachedPreOrderLimit();
                const willBePreOrder = this.willNextItemBePreOrder();
                
                if (hasReachedLimit) {
                    // No more capacity available
                    // console.log('Pre-order limit reached, disabling button');
                    addToCartButton.textContent = 'Sold Out';
                    addToCartButton.setAttribute('disabled', 'disabled'); // Set disabled attribute
                    addToCartButton.classList.add('disabled');
                    this.product.selected_or_first_available_variant.addToCartButton = false;
                } else if (willBePreOrder) {
                    // Next item(s) will be pre-order
                    // console.log('Will be pre-order, enabling button');
                    addToCartButton.textContent = 'Pre-Order Now';
                    addToCartButton.removeAttribute('disabled');
                    addToCartButton.classList.remove('disabled');
                    addToCartButton.classList.add('is-preorder');
                    this.product.selected_or_first_available_variant.addToCartButton = true;
                } else {
                    // Regular inventory available
                    // console.log('Regular inventory available, enabling button');
                    addToCartButton.textContent = 'Add to Cart';
                    addToCartButton.removeAttribute('disabled');
                    addToCartButton.classList.remove('disabled');
                    addToCartButton.classList.remove('is-preorder');
                    this.product.selected_or_first_available_variant.addToCartButton = true;
                }

                // Log final button state for debugging
                // console.log('Final button state:', {
                //     text: addToCartButton.textContent,
                //     hasDisabledAttribute: addToCartButton.hasAttribute('disabled'),
                //     hasDisabledClass: addToCartButton.classList.contains('disabled')
                // });
            } catch (error) {
                console.error('Liquify - Error in updateAddToCartButton:', error);
            }
        },

        findFirstAvailableOption(optionName) {
            try {
                const form = this.$el.closest('form') || this.$el.querySelector('form');
                if (!form) {
                console.warn('Liquify - Form not found in findFirstAvailableOption');
                return null;
                }
                
                // Get all options for this option name
                const selector = this.selectorType === 'radio' 
                ? `[data-productVariantsLoop] input[name='${optionName}']`
                : `[data-productVariantsLoop] select[name='${optionName}'] option`;
                
                const options = [...form.querySelectorAll(selector)];
                
                // Find the first available one
                const firstAvailable = options.find(option => 
                this.isVariantAvailable(option.value, optionName)
                );
                
                // If no available options found, return the first one
                return firstAvailable || options[0] || null;
            } catch (error) {
                console.error('Liquify - Error in findFirstAvailableOption:', error);
                return null;
            }
        },

        setInputAvailability(listOfOptions, listOfAvailableOptions) {
            listOfOptions.forEach((input) => {
                if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
                    input.classList.remove('data-disabled');
                    input.removeAttribute('data-disabled');
                    if (this.selectorType === 'select') {
                        input.removeAttribute("disabled");
                    }
                } else {
                    input.classList.add('data-disabled');
                    input.setAttribute('data-disabled', '');
                    if (this.selectorType === 'select') {
                        input.setAttribute("disabled", "true");
                    }
                }
            });

        },

        // Add this method to check if a variant is in pre-order status
        isPreOrder(quantityToAdd = 1) {
            try {
                // Get the current variant
                const variant = this.product.selected_or_first_available_variant;
                if (!variant) return false;
                
                // Get inventory data
                const inventoryData = this.getVariantInventoryData(variant.id);
                if (!inventoryData) return false;
                
                const inventoryQuantity = inventoryData.inventory_quantity || 0;
                const preOrderLimit = inventoryData.pre_order_limit || 5;
                const cartQuantity = this.getVariantInCartQuantity(variant.id);
                
                // console.log(`Is pre-order check: variant=${variant.title}, inventory=${inventoryQuantity}, in cart=${cartQuantity}, limit=${preOrderLimit}`);
                
                // Check if we've reached the total limit first
                const totalCapacity = inventoryQuantity + preOrderLimit;
                if (cartQuantity >= totalCapacity) {
                    // console.log('Reached total capacity, not available for pre-order');
                    return false;
                }
                
                // NEW LOGIC:
                // If cart quantity is less than inventory, it's regular purchase
                // If cart quantity is >= inventory but < total capacity, it's pre-order
                const isPreOrderItem = (cartQuantity >= inventoryQuantity) && (cartQuantity < totalCapacity);
                
                // console.log(`Is pre-order mode: ${isPreOrderItem}`);
                // console.log('----------------------------------------');
                
                return isPreOrderItem;
            } catch (error) {
                // console.error('Liquify - Error in isPreOrder:', error);
                // console.log('----------------------------------------');
                return false;
            }
        },

        // Add helper method to get inventory data
        getVariantInventoryData(variantId) {
            // console.log(`Getting inventory data for variant ${variantId}`);
            try {
                const dataElement = document.getElementById(`variant-inventory-${variantId}`);
                if (!dataElement) {
                    // console.log(`No inventory data element found for variant ${variantId}`);
                    return null;
                }
                
                const data = JSON.parse(dataElement.textContent);
                // console.log(`Inventory data retrieved for variant ${variantId}:`, data);
                return data;
            } catch (error) {
                console.error(`Error getting inventory data for variant ${variantId}:`, error);
                return null;
            }
        },

        getVariantShippingData(variantId) {
            // console.log(`Getting inventory data for variant ${variantId}`);
            try {
                const dataElement = document.getElementById(`variant-shipping-${variantId}`);
                console.log('dataElement', dataElement);
                if (!dataElement) {
                    // console.log(`No inventory data element found for variant ${variantId}`);
                    return null;
                }
                
                const data = JSON.parse(dataElement.textContent);
                // console.log(`Inventory data retrieved for variant ${variantId}:`, data);
                return data;
            } catch (error) {
                console.error(`Error getting inventory data for variant ${variantId}:`, error);
                return null;
            }
        },

        logVariantData() {
            const variant = this.product.selected_or_first_available_variant;
            // console.log('Current variant:', variant);
            
            // Try to get additional data
            const inventoryData = this.getVariantInventoryData(variant.id);
            // console.log('Inventory data from embedded script:', inventoryData);
            
            // List all properties
            // console.log('All variant properties:');
            for (const key in variant) {
                // console.log(`${key}: ${variant[key]}`);
            }
        },

        getVariantInCartQuantity(variantId) {
            // console.log(`Checking cart quantity for variant ${variantId}`);
    
            // Handle null or undefined
            if (!variantId) {
                console.warn('Invalid variant ID provided to getVariantInCartQuantity');
                return 0;
            }
            
            // Ensure we have cart items
            if (!this.cartItems || !this.cartItems.length) {
                // console.log('No cart items available');
                return 0;
            }
            
            // Convert to number for consistent comparison
            const numericId = parseInt(variantId);
            
            // Log all cart items for debugging
            // console.log('All cart items:');
            this.cartItems.forEach(item => {
                // console.log(`- ID: ${item.variant_id} (${typeof item.variant_id}), Product: ${item.product_title}, Variant: ${item.variant_title}, Qty: ${item.quantity}`);
            });
            
            const cartItem = this.cartItems.find(item => {
                const itemId = parseInt(item.variant_id);
                const match = itemId === numericId;
                // console.log(`Comparing cart item ${itemId} with ${numericId}: ${match}`);
                return match;
            });
            
            const quantity = cartItem ? cartItem.quantity : 0;
            // console.log(`Found quantity in cart for variant ${variantId}: ${quantity}`);
            return quantity;
        },

        getRemainingPreOrderCapacity() {
            try {
                const variant = this.product.selected_or_first_available_variant;
                if (!variant) {
                    console.warn('No variant selected');
                    return 0;
                }
                
                // Get inventory data
                const inventoryData = this.getVariantInventoryData(variant.id);
                if (!inventoryData) {
                    console.warn(`No inventory data available for variant ${variant.id}`);
                    return 0;
                }
                
                const inventoryQuantity = inventoryData.inventory_quantity || 0;
                const preOrderLimit = inventoryData.pre_order_limit || 5;
                
                // Fetch cart quantities specifically for this check
                // console.log('Getting cart quantity for capacity calculation...');
                const cartQuantity = this.getVariantInCartQuantity(variant.id);
                
                // console.log(`CAPACITY CALCULATION: 
                //     Variant: ${variant.title} 
                //     Inventory: ${inventoryQuantity} 
                //     Pre-order limit: ${preOrderLimit} 
                //     In cart: ${cartQuantity}`);
                
                // FIXED: Always consider total capacity as regular inventory + pre-order limit
                const totalCapacity = inventoryQuantity + preOrderLimit;
                const remainingCapacity = totalCapacity - cartQuantity;
                
                // console.log(`Total capacity calculation: ${inventoryQuantity} + ${preOrderLimit} = ${totalCapacity}`);
                // console.log(`Remaining capacity: ${totalCapacity} - ${cartQuantity} = ${remainingCapacity}`);
                
                return remainingCapacity;
            } catch (error) {
                // console.error('Error calculating remaining capacity:', error);
                return 0;
            }
        },

        // Get estimated delivery date for pre-orders (can customize this as needed)
        getPreOrderDeliveryDate() {
            // Default to 2 weeks from now
            const date = new Date();
            date.setDate(date.getDate() + 14);

            //overwrite using the custom metafield value of the product itself
            let shippingDateMetafield = this.product.metafields?.joule?.pre_order_shipping_date;
            //overwrite using variant-specific metafield value of selected item
            let variantShippingDateMetafield = this.product.selected_or_first_available_variant.metafields?.joule?.pre_order_shipping_date;

            if (variantShippingDateMetafield) {
                shippingDateMetafield = variantShippingDateMetafield;
            }

            if (shippingDateMetafield) {
                date.setDate(new Date(shippingDateMetafield).getDate());
            }

            // Format as Month Day
            return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        },


        // Add this method to check if the requested quantity is available
        canAddToCart(quantity = 1) {
            const variant = this.product.selected_or_first_available_variant;
            if (!variant) return false;
            
            if (this.isPreOrder(quantity)) {
                // Pre-order check already includes quantity validation
                return true;
            } else if (variant.available) {
                // For regular inventory, check if we have enough
                return variant.inventory_quantity >= quantity;
            }
            
            return false;
        },

        /**
         * Called by x-init.
         *
         * @see ProductVariationsContainer
         */
        activateVariants() {
            if(this.$el.querySelectorAll('[li-element="product-option-select"]').length > 0) {
                this.selectorType = 'select'
            }
            const checkboxes = this.$el.querySelectorAll("[li-element=product-option-input]");
            checkboxes.forEach(checkbox => {
                checkbox.addEventListener('change', (event) => {
                    // Throttle rapid changes to prevent excessive DOM updates
                    if (this._changeThrottle) {
                        clearTimeout(this._changeThrottle);
                    }
                    
                    this._changeThrottle = setTimeout(() => {
                        this.parentNode?.parentNode?.querySelectorAll("[li-element=product-option-input]").forEach(element => {
                            element.previousElementSibling.classList.remove('w--redirected-checked', 'w--redirected-focus')
                        })

                        if (event.target.checked) {
                            this.previousElementSibling?.classList.add('w--redirected-checked', 'w--redirected-focus')
                        }

                        // Make sure we fully update when a variant is selected
                        if (event.target.checked) {
                            this.setVariant(true);
                            
                            // Force a refresh of the pre-order status
                            this.$nextTick(() => {
                                this.logPreOrderStatus();
                                this.updateAddToCartButton(this.$el.closest('form'));
                            });
                        }
                    }, 50); // Throttle by 50ms
                });
            });
            this.observeVariants();
   
            // Fetch cart data
            this.fetchCart().then(() => {
                // console.log('Cart data loaded for product item');
                
                // CHANGED: Now we find first available options
                let option1 = this.product.options.length > 0
                ? this.findFirstAvailableOption(this.product.options?.[0])
                : false;

                let option2 = this.product.options.length > 1
                ? this.findFirstAvailableOption(this.product.options?.[1])
                : false;

                let option3 = this.product.options.length > 2
                ? this.findFirstAvailableOption(this.product.options?.[2])
                : false;
                
                // Activate the first available options
                if (option1) {
                this.activateVariant(option1);
                // Trigger click event to ensure variant is selected
                if (option1.tagName === 'INPUT') {
                    option1.checked = true;
                    option1.dispatchEvent(new Event('change', { bubbles: true }));
                } else if (option1.tagName === 'OPTION') {
                    option1.selected = true;
                    option1.closest('select').dispatchEvent(new Event('change', { bubbles: true }));
                }
                }
                
                if (option2) {
                this.activateVariant(option2);
                }
                
                if (option3) {
                this.activateVariant(option3);
                }
                
                // Update statuses after selecting the first available options
                this.updateVariantStatuses();
                this.setVariant(false); // Don't push to history on initial load
            });
            
            // Setup firstOptionHasAvailableChildren
            this.product.variants.map((variant) => {
                if (this.firstOptionHasAvailableChildren[variant?.option1] === undefined) {
                this.firstOptionHasAvailableChildren[variant?.option1] = 0;
                }
                
                // IMPROVED: Use our availability logic for colors too
                // Use isVariantAvailable instead of just variant.available
                const optionName = this.product.options?.[0];
                const isAvailable = this.isVariantAvailable(variant.option1, optionName);
                this.firstOptionHasAvailableChildren[variant?.option1] += isAvailable ? 1 : 0;
            });
            
            // Add CSS for disabled state
            const css = ' [data-ProductVariantsLoop] input[type=radio].data-disabled + span{ ' +
                ' text-decoration: line-through; ' +
                ' opacity: .5; ' +
                ' pointer-events: none; ' +
                ' } ' +
                ' .button.max-width-full.w-button:data-disabled, ' +
                ' .button.max-width-full.w-button[data-disabled] { ' +
                '  cursor: not-allowed; ' +
                ' }';
            const style = document.createElement("style")
            const head = document.getElementsByTagName('head')[0]
            head.appendChild(style)
            style.appendChild(document.createTextNode(css));
        },

        activateVariant(element) {
            element.parentNode.parentNode.querySelectorAll("[li-element=product-option-input]").forEach(element => {
                element.previousElementSibling.classList.remove('w--redirected-checked', 'w--redirected-focus')
            })
            element.previousElementSibling.classList.add('w--redirected-checked')

        },

        quickAddToCart() {
            // Make sure a variant is selected
            if (!this.product.selected_or_first_available_variant?.available) {
                // console.log('Liquify - Variant not available');
                return;
            }

            // Prepare the data for adding to cart
            const payload = {
                items: [{
                    id: this.product.selected_or_first_available_variant.id,
                    quantity: 1  // Adjust quantity as needed
                }]
            };

            // Initiate the fetch request to add the item to the cart
            fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            })
            .then(response => response.json())
            .then(data => {
                // console.log('Liquify - Item added to cart:', data);
                this.$dispatch('cartupdated', data.items);
                this.$dispatch('showcartmessage', {
                    status: data.status,
                    message: data.message,
                    description: data.description
                });
            })
            .catch(error => {
                console.error('Liquify - Quick add failed:', error);
                this.$dispatch('showcartmessage', {
                    status: error.status,
                    message: error.message,
                    description: error.description
                });
            });
        },

        /**
         * Retrieves the selected element from the transferred option group
         */
        // getVariantElement(optionName) {
        //     // Innerhalb von $nextTick(() => { activateVariants() }) bezieht sich this.$el auf den Container des Alpine-Objekts.
        //     // Bei einer Variantenauswahl hingegen verweist this.$el auf das Element, das die Auswahl ausgelöst hat.
        //     let form = this.$el.closest('form') ?? this.$el.querySelector('form');

        //     let element = form?.querySelector("[data-productVariantsLoop] input[name='" + optionName + "']:checked");
        //     if (element && element?.getAttribute('data-disabled') !== null) {
        //         element = form.querySelector("[data-productVariantsLoop] input[name='" + optionName + "']:not([data-disabled])")
        //     }
        //     //let selector = this.formSelector + " [data-productVariantsLoop] input[name='" + optionName + "']:checked";

        //     if (optionName !== undefined && this.selectorType === 'select') {
        //         element = form?.querySelector("[data-productVariantsLoop] select[name='" + optionName + "'] option:checked")

        //         if (element && element?.getAttribute('data-disabled') !== null) {
        //             element = form.querySelector("[data-productVariantsLoop] select[name='" + optionName + "'] option:not([data-disabled])")
        //         }
        //     }
        //     return element ?? '';
        // },

        getVariantElement(optionName) {
            // Try to get the form
            let form = null;
            try {
                form = this.$el.closest('form') || this.$el.querySelector('form');
            } catch (error) {
                console.error('Liquify - Error getting form:', error);
                return '';
            }

            if (!form) {
                console.warn('Liquify - Form not found in getVariantElement');
                return '';
            }

            let element = null;
            try {
                // Try to get the selected element
                element = form.querySelector("[data-productVariantsLoop] input[name='" + optionName + "']:checked");
                if (element && element?.getAttribute('data-disabled') !== null) {
                    element = form.querySelector("[data-productVariantsLoop] input[name='" + optionName + "']:not([data-disabled])");
                }

                if (optionName !== undefined && this.selectorType === 'select') {
                    element = form.querySelector("[data-productVariantsLoop] select[name='" + optionName + "'] option:checked");

                    if (element && element?.getAttribute('data-disabled') !== null) {
                        element = form.querySelector("[data-productVariantsLoop] select[name='" + optionName + "'] option:not([data-disabled])");
                    }
                }
            } catch (error) {
                console.error('Liquify - Error finding variant element:', error);
            }

            return element || '';
        },

        filterVariants() {
            const option1 = this.product.options.length > 0
                ? this.getVariantElement(this.product.options?.[0])
                : false;

            const option2 = this.product.options.length > 1
                ? this.getVariantElement(this.product.options?.[1])
                : false;

            const option3 = this.product.options.length > 2
                ? this.getVariantElement(this.product.options?.[2])
                : false;

            if (option1) {
                this.activateVariant(option1)
            }
            if (option2) {
                this.activateVariant(option2)
            }
            if (option3) {
                this.activateVariant(option3)
            }

            let selectedOptions = [
                option1?.value,
                option2?.value,
                option3?.value,
            ];
            selectedOptions = (selectedOptions.filter(i => i)).join('-')

            let possibleProducts = []
            for (const variant of this.product.variants) {
                let variantString = [
                    option1?.value ? variant?.option1 : undefined,
                    option2?.value ? variant?.option2 : undefined,
                    option3?.value ? variant?.option3 : undefined,
                ];
                variantString = (variantString.filter(i => i)).join('-')

                if (selectedOptions === variantString) {
                    possibleProducts.push(variant)
                }
            }

            return possibleProducts;
        },

        setUrlParam() {
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set('variant', this.product.selected_or_first_available_variant.id);

            window.history.pushState({ ...window.history.state}, '', '?' + urlParams.toString());
        },

        findAvailableVariant(allowUnavailable = false) {
            const matchedProducts = this.filterVariants()

            if (matchedProducts.length < 1) {
                console.info('Liquify - No matching variant found, using initial variant')
                return this.product.initial_selected_or_first_available_variant
            }

            if (allowUnavailable || matchedProducts[0].available) {
                return matchedProducts[0];
            }
            
            // Look for any available variant
            const availableVariant = matchedProducts.find(variant => variant.available);
            if (availableVariant) return availableVariant;

            return this.product.initial_selected_or_first_available_variant;
        },

        // setVariant(pushState = true) {
        //     this.updateVariantStatuses()
        //     const product = this.findAvailableVariant()

        //     if (! product) {
        //         console.log('Liquify - No product found to set active')

        //         return
        //     }

        //     console.log('Liquify - Set active variant', product)

        //     this.product.selected_or_first_available_variant.addToCartButton = true

        //     this.product.selected_or_first_available_variant = {...this.product.selected_or_first_available_variant, ...product};
        //     this.product.selected_or_first_available_variant.featured_image = product?.featured_image?.src
        //     this.product.selected_or_first_available_variant.price = product?.price;

        //     // nur für die Detailseiten
        //     if (pushState && this.isProductPage) {
        //         this.setUrlParam()
        //     }
        // },

        setVariant(pushState = true, allowUnavailable = false) {
            try {
                // Prevent recursive updates
                if (this._isSettingVariant) return;
                this._isSettingVariant = true;
                
                console.log('Liquify - setVariant called, allowUnavailable=', allowUnavailable);
                
                // Update statuses of all variant options
                this.updateVariantStatuses();
                
                // Find matching variant for current selection
                const product = this.findAvailableVariant(allowUnavailable);

                if (!product) {
                    console.log('Liquify - No product found to set active');
                    this._isSettingVariant = false;
                    return;
                }

                // Check if this is a waitlist-eligible variant
                const isUnavailableVariant = !product.available || 
                    (allowUnavailable && (this.hasReachedPreOrderLimit() || !this.isVariantAvailable(product.option2, 'Size')));
                
                console.log('Variant selected:', product.title, 
                        'Available:', product.available,
                        'Is unavailable:', isUnavailableVariant);

                // Update the selected variant
                this.product.selected_or_first_available_variant = {
                    ...product,
                    featured_image: product?.featured_image?.src,
                    quantity: this.product.selected_or_first_available_variant.quantity || 1,
                    addToCartButton: !isUnavailableVariant
                };

                // Only log and update UI after all data changes are complete
                this.$nextTick(() => {
                    let form = this.$el.closest('form') || this.$el.querySelector('form');
                    this.updateAddToCartButton(form);
                    this.logPreOrderStatus();
                    
                    // IMPORTANT: Directly check and update waitlist button
                    if (isUnavailableVariant) {
                        console.log('Triggering waitlist eligibility check for unavailable variant');
                        
                        // Try both event dispatch and direct function call
                        document.dispatchEvent(new CustomEvent('variant:unavailable-selected', {
                            detail: { 
                                variantId: product.id,
                                productId: this.product.id 
                            }
                        }));

                        console.log('Triggering waitlist eligibility check for unavailable variant');
                        
                        // Direct function call as backup
                        if (typeof window.updateWaitlistButton === 'function') {
                            window.updateWaitlistButton(true, product.id);
                        }
                    }
                    
                    // Reset the flag after updates are complete
                    setTimeout(() => { this._isSettingVariant = false; }, 50);
                });

                // Update URL if needed
                if (pushState && this.isProductPage) {
                    this.setUrlParam();
                }
            } catch (error) {
                console.error('Liquify - Error in setVariant:', error);
                this._isSettingVariant = false;
            }
        },

        // Add this new method to update the button text
        updateAddToCartButtonText() {
            try {
                // Find the button element
                const button = this.$el.querySelector('.product-header_ad-to-cart-wrapper .button.max-width-full.w-button');
                if (!button) return;

                // Update text based on pre-order status
                if (this.isPreOrder()) {
                    button.textContent = 'Pre-Order Now';
                } else if (this.product.selected_or_first_available_variant.available) {
                    button.textContent = 'Add to cart';
                } else {
                    button.textContent = 'Not in Stock';
                }
            } catch (error) {
                console.error('Liquify - Error updating button text:', error);
            }
        },

        getAddToCartBody(products) {
            return JSON.stringify({
                'items': products.map(product => ({
                    id: product.id,
                    quantity: product.quantity ?? 1,
                }))
            });
        },

        callAddToCartApi(body) {
            return fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: body
            })
        },

        /**
         * Add all products to cart.
         */
        async addToCartWithBundle(toggleMiniCart = true) {
            // console.log('Liquify - addToCartWithBundle()', toggleMiniCart);

            // Map all bundle products
            const products = Object.entries(this.bundle).map(([key, value]) => ({
                id: value,
                quantity: 1
            }))

            // Add selected product
            products.push({
                id: this.product.selected_or_first_available_variant.id ?? this.product.selected_or_first_available_variant?.id,
                quantity: this.product.selected_or_first_available_variant.quantity ?? 1,
            })

            // Create body with all products
            let body = this.getAddToCartBody(products);

            // Single API call for all products
            try {
                let response = await this.callAddToCartApi(body);
                let data = await response.json();
                // console.log('Liquify - Items added (addToCartWithBundle()):', data.items);
                this.products = data.items;

                LiquifyHelper.handleTriggerClick();

                this.$dispatch('cartupdated');
                if (toggleMiniCart) {
                    this.$dispatch('toggleminicart');
                }
                this.$dispatch('showcartmessage', {
                    status: data.status,
                    message: data.message,
                    description: data.description
                });
            } catch (error) {
                console.error('Liquify - Error in add to cart with bundle:', error);
                this.$dispatch('showcartmessage', {
                    status: error.status,
                    message: error.message,
                    description: error.description
                });
            }
        },

        logPreOrderStatus() {
            const variant = this.product.selected_or_first_available_variant;
            // console.log('----------------------------------------');
            // console.log('Pre-order status check:');
            // console.log('Variant ID:', variant.id);
            // console.log('Variant Title:', variant.title);
            // console.log('Available:', variant.available);
            // console.log('Inventory Management:', variant.inventory_management);
            // console.log('Variant object', variant);
            // // console.log('Inventory Quantity:', variant.inventory_quantity);
            // console.log('Is Pre-Order:', this.isPreOrder());
            // console.log('----------------------------------------');
        },

        debugPreOrderSystem() {
            console.group('Pre-Order System Debug');
            try {
                // Log cart status
                // console.log('Cart items:', this.cartItems);
                
                // Get current variant
                const variant = this.product.selected_or_first_available_variant;
                // console.log('Current variant:', variant);
                
                if (variant) {
                    // Try to get inventory data
                    const inventoryData = this.getVariantInventoryData(variant.id);
                    // console.log('Inventory data:', inventoryData);
                    
                    // Calculate quantities
                    const inventoryQuantity = inventoryData?.inventory_quantity || 0;
                    const preOrderLimit = inventoryData?.pre_order_limit || 5;
                    const cartQuantity = this.getVariantInCartQuantity(variant.id);
                    
                    // console.log(`Variant ${variant.title} (${variant.id}):`);
                    // console.log(`- Inventory Quantity: ${inventoryQuantity}`);
                    // console.log(`- Pre-Order Limit: ${preOrderLimit}`);
                    // console.log(`- Quantity in Cart: ${cartQuantity}`);
                    
                    // Calculate remaining capacity
                    let remainingCapacity = 0;
                    if (inventoryQuantity > 0) {
                        remainingCapacity = inventoryQuantity - cartQuantity;
                        // console.log(`- Regular inventory remaining: ${remainingCapacity}`);
                    } else {
                        remainingCapacity = preOrderLimit + inventoryQuantity - cartQuantity;
                        // console.log(`- Pre-order capacity remaining: ${remainingCapacity}`);
                    }
                    
                    // Is it a pre-order item?
                    const isPreOrderItem = inventoryData?.inventory_management === "shopify" && 
                                        inventoryData?.inventory_policy === "continue" &&
                                        inventoryQuantity <= 0 &&
                                        remainingCapacity > 0;
                                        
                    // console.log(`- Is pre-order item: ${isPreOrderItem}`);
                    // console.log(`- Has reached limit: ${remainingCapacity <= 0}`);
                } else {
                    // console.log('No selected variant available');
                }
            } catch (error) {
                // console.error('Error in debug logging:', error);
            }
            console.groupEnd();
        },

        isVariantAvailable(optionValue, optionName) {
            try {
                // Get all variants with this option value
                const matchingVariants = this.product.variants.filter(variant => {
                    const optionIndex = this.product.options.indexOf(optionName);
                    return variant[`option${optionIndex + 1}`] === optionValue;
                });
                
                // console.log(`Checking availability for ${optionName}=${optionValue}, found ${matchingVariants.length} matching variants`);
                
                // Check if any of those variants are available
                for (const variant of matchingVariants) {
                    // console.log(`Checking variant ${variant.title}, available=${variant.available}`);
                    
                    // Get inventory data for additional checks
                    const inventoryData = this.getVariantInventoryData(variant.id);
                    if (!inventoryData) {
                        // console.log(`No inventory data for ${variant.title}, using native availability`);
                        if (!variant.available) continue; // Skip if Shopify says it's unavailable
                        return variant.available;
                    }
                    
                    const inventoryQuantity = inventoryData.inventory_quantity || 0;
                    const inventoryPolicy = inventoryData.inventory_policy || "deny";
                    const preOrderLimit = inventoryData.pre_order_limit || 0;
                    const cartQuantity = this.getVariantInCartQuantity(variant.id);
                    
                    // console.log(`Variant details: inventory=${inventoryQuantity}, policy=${inventoryPolicy}, limit=${preOrderLimit}, cart=${cartQuantity}`);
                    
                    // KEY FIX: Check total capacity including pre-order limit
                    const totalCapacity = inventoryQuantity + (inventoryPolicy === "continue" ? preOrderLimit : 0);
                    
                    // If cart quantity >= total capacity, this option is sold out
                    if (cartQuantity >= totalCapacity) {
                        // console.log(`${variant.title} sold out - cart quantity ${cartQuantity} >= total capacity ${totalCapacity}`);
                        continue; // This variant is sold out, try next one
                    }
                    
                    // At this point we know there's capacity available
                    // console.log(`${variant.title} has capacity available: ${totalCapacity - cartQuantity} remaining`);
                    return true;
                }
                
                // If we get here, no matching variants are available
                // console.log(`No available variants found for ${optionName}=${optionValue}`);
                return false;
            } catch (error) {
                console.error('Error checking variant availability:', error);
                return false; // Default to unavailable in case of error
            }
        },

        willNextItemBePreOrder(quantityToAdd = 1) {
            try {
                const variant = this.product.selected_or_first_available_variant;
                if (!variant) return false;
                
                // Get inventory data
                const inventoryData = this.getVariantInventoryData(variant.id);
                if (!inventoryData) return false;
                
                const inventoryQuantity = inventoryData.inventory_quantity || 0;
                const cartQuantity = this.getVariantInCartQuantity(variant.id);
                
                // If adding this quantity would go beyond regular inventory, 
                // at least some items would be pre-order
                return (cartQuantity + quantityToAdd) > inventoryQuantity;
            } catch (error) {
                console.error('Error checking if next item will be pre-order:', error);
                return false;
            }
        },

        hasAnyAvailableVariants() {
            try {
                // Check if the selected variant is available
                const selectedVariant = this.product.selected_or_first_available_variant;
                if (selectedVariant) {
                    const inventoryData = this.getVariantInventoryData(selectedVariant.id);
                    if (!inventoryData) {
                        // console.log('No inventory data for selected variant, using native availability');
                        return selectedVariant.available;
                    }
                    
                    const inventoryQuantity = inventoryData.inventory_quantity || 0;
                    const inventoryPolicy = inventoryData.inventory_policy || "deny";
                    const preOrderLimit = inventoryData.pre_order_limit || 0;
                    const cartQuantity = this.getVariantInCartQuantity(selectedVariant.id);
                    
                    // Regular inventory available
                    if (inventoryQuantity > 0 && (inventoryQuantity - cartQuantity) > 0) {
                        return true;
                    }
                    
                    // Pre-order available
                    if (inventoryPolicy === "continue" && preOrderLimit > 0) {
                        const remainingPreOrder = preOrderLimit - cartQuantity;
                        if (remainingPreOrder > 0) {
                            return true;
                        }
                    }
                }
                
                // If the selected variant isn't available, check if any other variant is
                for (const variant of this.product.variants) {
                    // Skip the current variant as we already checked it
                    if (variant.id === selectedVariant?.id) continue;
                    
                    const inventoryData = this.getVariantInventoryData(variant.id);
                    if (!inventoryData) {
                        if (variant.available) return true;
                        continue;
                    }
                    
                    const inventoryQuantity = inventoryData.inventory_quantity || 0;
                    const inventoryPolicy = inventoryData.inventory_policy || "deny";
                    const preOrderLimit = inventoryData.pre_order_limit || 0;
                    const cartQuantity = this.getVariantInCartQuantity(variant.id);
                    
                    // Regular inventory available
                    if (inventoryQuantity > 0 && (inventoryQuantity - cartQuantity) > 0) {
                        return true;
                    }
                    
                    // Pre-order available
                    if (inventoryPolicy === "continue" && preOrderLimit > 0) {
                        const remainingPreOrder = preOrderLimit - cartQuantity;
                        if (remainingPreOrder > 0) {
                            return true;
                        }
                    }
                }
                
                return false;
            } catch (error) {
                console.error('Error in hasAnyAvailableVariants:', error);
                return false;
            }
        },

        /**
         * Add a single product to cart.
         */
        async addToCart(e, toggleMiniCart = true, productId = undefined, quantity = undefined) {
            // Skip if we're processing a cart update already
            if (this._isProcessingCartUpdate) {
                // console.log('Skipping addToCart during cart update processing');
                return;
            }
            
            if (!e || !(e instanceof Event) || typeof e.preventDefault !== 'function') {
                e = window.event;
            }
            e.preventDefault();
            
            const requestedQuantity = quantity ?? this.quantity ?? 1;
            // console.log('Liquify - Add to cart called for variant:', this.product.selected_or_first_available_variant?.id);
            
            // First check if we've reached the pre-order limit
            if (this.hasReachedPreOrderLimit()) {
                // console.log('Liquify - Cannot add to cart: pre-order limit reached');
                this.$dispatch('showcartmessage', { 
                    status: 'error', 
                    message: 'This item is sold out', 
                    description: 'The pre-order limit has been reached.'
                });
                return;
            }
            
            // Get variant info and cart state
            const variant = this.product.selected_or_first_available_variant;
            const inventoryData = this.getVariantInventoryData(variant.id);
            const inventoryQuantity = inventoryData?.inventory_quantity || 0;
            const cartQuantity = this.getVariantInCartQuantity(variant.id);
            
            // Determine how many items are regular and how many are pre-order
            let regularItems = 0;
            let preOrderItems = 0;
            
            if (cartQuantity < inventoryQuantity) {
                // Some regular inventory is still available
                regularItems = Math.min(requestedQuantity, inventoryQuantity - cartQuantity);
                preOrderItems = requestedQuantity - regularItems;
            } else {
                // All items will be pre-order
                preOrderItems = requestedQuantity;
            }
            
            // console.log(`Adding to cart: regular=${regularItems}, pre-order=${preOrderItems}`);
            
            // Get form data
            let form = this.$el.closest('form') ?? this.$el.querySelector('form');
            const formData = LiquifyHelper.serializeFormToObject(form);

            //get shipping date using the helper function getvariantshippingdata
            const shippingData = this.getVariantShippingData(variant.id);
            console.log('shipping data is', shippingData, variant.id);
            //fallback to 3 days in the future
            const threeDaysFromNow = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            const shippingDate = shippingData?.shipping_date || threeDaysFromNow;
            console.log('Liquify - Shipping date for variant', variant.id, 'is', shippingDate);
            const formattedShippingDate = new Date(shippingDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
            console.log('Liquify - Formatted shipping date:', formattedShippingDate);

            //if shipping date is more than 3 days in the future, always consider it a pre-order
            const today = new Date();
            const threeDaysLater = new Date();
            threeDaysLater.setDate(today.getDate() + 3);
            if (new Date(shippingDate) > threeDaysLater) {
                preOrderItems = requestedQuantity;
                regularItems = 0;
            }
            
            // Add pre-order properties if needed
            let properties = {};
            if (preOrderItems > 0) {
                properties = { 
                    'Pre-Order': `This item will ship by ${formattedShippingDate}`,
                    '_is_preorder': 'true',
                    '_shipped_by': shippingDate,
                };
                // console.log('Liquify - Adding with pre-order properties:', properties);
            } else {
                properties = { 
                    '_is_preorder': 'false',
                    '_shipped_by': shippingDate,
                };
            }
            
            const variantId = productId ?? variant.id;
            
            // console.log(`Liquify - Adding to cart: variant=${variantId}, quantity=${requestedQuantity}`);
            
            const payload = {
                "items": [{
                    id: variantId,
                    quantity: requestedQuantity,
                    properties: properties,
                    ...formData
                }]
            };

            let body = JSON.stringify(payload);

            try {
                const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: body
                });
                
                const data = await response.json();
                
                if (data.status && data.status !== 200) {
                    console.error('Liquify - Error adding to cart:', data);
                    this.$dispatch('showcartmessage', { 
                        status: data.status, 
                        message: data.message || 'Error adding item to cart', 
                        description: data.description || 'Please try again'
                    });
                    return;
                }
                
                this.products = data.items;
                // console.log('Liquify - Product(s) added successfully:', data.items);

                // After successful add, trigger the event safely
                this.triggerCartUpdated();

                // Refresh cart data
                this.fetchCart().then(() => {
                    // After fetching the cart, update the UI to reflect current availability
                    this.$nextTick(() => {
                        // Force UI update based on new cart state
                        this.hasReachedPreOrderLimit();
                    });
                    
                    // console.log('Cart fetched during initialization');
                    this.debugPreOrderSystem();
                });
                
                this.$dispatch('cartupdated', this.products);

                if (toggleMiniCart) {
                    this.$dispatch('toggleminicart');
                }

                this.$dispatch('showcartmessage', { 
                    status: 'success', 
                    message: this.isPreOrder() ? 'Pre-order added to cart' : 'Item added to cart',
                    description: '' 
                });
            } catch (error) {
                console.error('Liquify - Error in addToCart:', error);
                this.$dispatch('showcartmessage', { 
                    status: 'error', 
                    message: 'Error adding item to cart', 
                    description: error.message || 'Please try again'
                });
            }
        },

        /**
         * Add a single product to cart and redirect to checkout.
         */
        async addToCartAndCheckout(e, toggleMiniCart = true, productId = undefined, quantity = undefined) {
            await this.addToCart(e, toggleMiniCart, productId, quantity)
                .then(() => {
                    window.location.href = '/checkout';
                });
        },

        /**
         * Format monetary values.
         */
        moneyFormat(value, minor = true) {
            return LiquifyHelper.moneyFormat(value, minor)
        },

        /**
         * @deprecated
         */
        get options_by_name() {
            //console.warn('deprecated "get options_by_name" in handleVariant')
            return this.product.options_by_name
        },
        /**
         * @deprecated
         */
        get options() {
            //console.warn('deprecated "get options" in handleVariant')
            return this.product.options
        },
        /**
         * @deprecated
         */
        get variants() {
            //console.warn('deprecated "get variants" in handleVariant')
            return this.product.variants
        },
        /**
         * @deprecated
         */
        get initial_selected_or_first_available_variant() {
            //console.warn('deprecated "get initial_selected_or_first_available_variant" in handleVariant')
            return this.product.initial_selected_or_first_available_variant
        },
        /**
         * @deprecated
         */
        get selected_or_first_available_variant() {
            //console.warn('deprecated "get selected_or_first_available_variant" in handleVariant')
            return this.product.selected_or_first_available_variant
        },
        /**
         * @deprecated
         */
        get variantImage() {
            //console.warn('deprecated "get variantImage" in handleVariant')
            return this.product.selected_or_first_available_variant.featured_image
        },
        /**
         * @deprecated
         */
        get selected_product_id() {
            //console.warn('deprecated "get options" in handleVariant')
            return this.product.selected_or_first_available_variant.id
        },
        /**
         * @deprecated
         */
        get quantity() {
            //console.warn('deprecated "get quantity" in handleVariant')
            return this.product.selected_or_first_available_variant.quantity
        },
        /**
         * @deprecated
         */
        set quantity(quantity) {
            this.product.selected_or_first_available_variant.quantity = quantity
        },
        /**
         * @deprecated
         */
        get addToCartButton() {
            //console.warn('deprecated "get addToCartButton" in handleVariant')
            return this.product.selected_or_first_available_variant.addToCartButton
        },
        /**
         * @deprecated
         */
        set price(value) {
            //console.warn('deprecated "set price" in handleVariant')
            this.product.selected_or_first_available_variant.price = value;
        },
        /**
         * @deprecated
         */
        get price() {
            //console.warn('deprecated "get price" in handleVariant')
            return this.product.selected_or_first_available_variant.price;
        },
    }));

    /**
     * Alpine-Store for Global Bundle.
     */
    Alpine.data('handleGlobalBundle', (useQuantity = true, shopMoneyFormat) => ({
        bundle: {
            items: [],
            title: '',
            index: 1,
            /**
             * Returns the total price of all products in the globalBundle.
             */
            get total() {
                return this.items.reduce((acc, item) => {
                    return acc + (item.product.price * item.quantity);
                }, 0);
            },
            get totalFormatted() {
                return LiquifyHelper.moneyFormat(this.total, true, shopMoneyFormat);
            }
        },

        initializePreOrderButtons() {
            try {
                // console.log('Liquify - Initializing pre-order buttons');
                
                // Check if we need to update any buttons on page load
                if (this.isPreOrder()) {
                    // console.log('Liquify - Initial variant is pre-order');
                    
                    // Find all potential add to cart buttons
                    const buttons = document.querySelectorAll('.button.max-width-full.w-button');
                    buttons.forEach(button => {
                        if (button.textContent.includes('Add to cart') || button.textContent.includes('Add to Cart')) {
                            button.textContent = 'Pre-Order Now';
                        }
                    });
                }
            } catch (error) {
                console.error('Liquify - Error in initializePreOrderButtons:', error);
            }
        },

        init() {
            this.loadGlobalBundle();
            this.loadBundleIndex();

            this.logPreOrderStatus();

            // // Fetch cart on page load
            // console.log('calling fetch cart');
            // this.fetchCart();
            
            // // Set up cart update listeners
            // document.addEventListener('cart:updated', () => {
            //     this.fetchCart();
            // });

            // Attach event listeners to size options
            // this.$el.querySelectorAll("[li-element=product-option-input]").forEach(element => {
            //     element.addEventListener('click', (event) => {
            //         if (event.currentTarget.checked) {
            //             // Activate variant so it's marked as selected
            //             this.activateVariant(event.currentTarget);
            //             console.log('clicked size option', event.currentTarget);
            //             // Trigger quick-add after selecting a size
            //             this.quickAddToCart();
            //         }
            //     });
            // });

            // this.observeVariants();

            // Initialize pre-order buttons
            this.$nextTick(() => {
                this.initializePreOrderButtons();
            });
        },
        handleAdd(event) {
            this.addToGlobalBundle(event.detail.variant, event.detail.product)
        },
        getGlobalBundleCartBody() {
            let globalBundle = this.getGlobalBundleFromLocalStorage();

            return JSON.stringify({
                'items': globalBundle.map(item => ({
                    id: item.variant.id,
                    quantity: item.quantity,
                    properties: {
                        'bundle': `${this.bundle.title} ${this.bundle.index}`,
                    }
                }))
            })
        },
        /**
         * @todo Refactoring: Duplication in handleVariant() store.
         */
        callAddToCartApi(body) {
            return fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: body
            })
        },

        async addGlobalBundleToCart() {
            const body = this.getGlobalBundleCartBody()

            await this.callAddToCartApi(body)
                .then(response => {
                    let data = response.json();

                    this.updateBundleIndex();
                    this.clearGlobalBundle();
                    LiquifyHelper.handleTriggerClick();

                    this.$dispatch('cartupdated');
                    this.$dispatch('toggleminicart');
                    this.$dispatch('showcartmessage', {
                        status: data.status,
                        message: data.message,
                        description: data.description
                    });
                });
        },

        clearGlobalBundle() {
            this.updateBundleObject([]);
        },

        /**
         * Adds a new product to the globalBundle.
         *
         * If quantities are enabled and the product is already in the bundle, increase the quantity by 1.
         */
        addToGlobalBundle(variant, product) {
            if (useQuantity) {
                this.addOrIncreaseGlobalBundleItem(
                    this.getNewGlobalBundleItem(variant, product, variant.quantity)
                );

                return;
            }

            // Add the product as a new item to the bundle.
            this.addNewGlobalBundleItem(
                this.getNewGlobalBundleItem(variant, product, variant.quantity)
            );
        },

        getNewGlobalBundleItem(variant, product, quantity = 1) {
            variant.quantity = quantity

            return {
                id: this.uniqueId(),
                variant: variant,
                quantity: variant.quantity,
                product: product
            }
        },

        /**
         * If the product is already in the bundle, increase the quantity by 1.
         */
        addOrIncreaseGlobalBundleItem(item) {
            let globalBundle = this.getGlobalBundleFromLocalStorage();
            let existingItem = globalBundle.find(bundleItem => bundleItem.variant.id === item.variant.id);

            if (existingItem) {
                let quantity = parseInt(existingItem.variant.quantity);
                quantity += item.variant.quantity;
                existingItem.quantity = quantity;
                existingItem.variant.quantity = quantity;
            } else {
                globalBundle.push(item);
            }

            this.updateBundleObject(globalBundle);
        },

        addNewGlobalBundleItem(item) {
            let globalBundle = this.getGlobalBundleFromLocalStorage();
            globalBundle.push(item);

            this.updateBundleObject(globalBundle);
        },

        /**
         * Update the Alpine globalBundle object and save it to local storage.
         */
        updateBundleObject(globalBundle) {
            this.bundle.items = globalBundle;
            localStorage.setItem('globalBundle', JSON.stringify(globalBundle));
        },

        /**
         * One method to handle the various ways to update a bundle item.
         *
         * @see ProductVariationsContainer
         */
        updateBundleItem(bundleItemId, quantity) {
            this.loadGlobalBundle();

            if (quantity === 0) {
                this.removeProductFromGlobalBundle(bundleItemId);
                return;
            }

            if (quantity === '-1') {
                this.decrementQuantityOfBundleItem(bundleItemId);
                return;
            }

            if (quantity === '+1') {
                this.incrementQuantityOfBundleItem(bundleItemId);
                return;
            }

            this.updateQuantityOfBundleItem(bundleItemId, quantity);
        },

        updateQuantityOfBundleItem(bundleItemId, quantity) {
            let bundle = this.getGlobalBundleFromLocalStorage();
            let bundleItem = bundle.find(bundleItem => bundleItem.id === bundleItemId);
            bundleItem.quantity = quantity;
            bundleItem.product.quantity = quantity;

            this.updateBundleObject(bundle);
        },

        decrementQuantityOfBundleItem(bundleItemId) {
            let bundle = this.getGlobalBundleFromLocalStorage();
            let bundleItem = bundle.find(bundleItem => bundleItem.id === bundleItemId);

            if (bundleItem.quantity === 1) {
                this.removeProductFromGlobalBundle(bundleItemId);

                return;
            }

            bundleItem.quantity -= 1;
            bundleItem.product.quantity -= 1;

            this.updateBundleObject(bundle);
        },

        incrementQuantityOfBundleItem(bundleItemId) {
            let bundle = this.getGlobalBundleFromLocalStorage();
            let bundleItem = bundle.find(bundleItem => bundleItem.id === bundleItemId);
            let quantity = parseInt(bundleItem.quantity)
            quantity += 1;
            bundleItem.quantity = quantity;
            bundleItem.product.quantity = quantity;

            this.updateBundleObject(bundle);
        },

        /**
         * Removes a product from the globalBundle.
         */
        removeProductFromGlobalBundle(bundleItemId) {
            let bundle = this.getGlobalBundleFromLocalStorage();
            bundle = bundle.filter(bundleItem => bundleItem.id !== bundleItemId);

            this.updateBundleObject(bundle);
        },

        /**
         * Get the globalBundle from local storage and set it to the globalBundle property.
         */
        loadGlobalBundle() {
            this.getBundleName();
            this.bundle.items = this.getGlobalBundleFromLocalStorage();
        },

        loadBundleIndex() {
            let bundleIndex = localStorage.getItem('globalBundleIndex');

            if (! bundleIndex) {
                localStorage.setItem('globalBundleIndex', '1');
                this.bundle.index = 1;

                return;
            }

            this.bundle.index = parseInt(bundleIndex);
        },

        getGlobalBundleFromLocalStorage() {
            return JSON.parse(localStorage.getItem('globalBundle')) ?? [];
        },

        getBundleName() {
            if (document.querySelector('[data-bundlename]')) {
                this.bundle.title = document.querySelector('[data-bundlename]')
                    .textContent
                    .trim();

                return
            }

            if (document.querySelector('[data-bundlename] *')) {
                this.bundle.title = document.querySelector('[data-bundlename] *')
                    .textContent
                    .trim();

                return
            }

            this.bundle.title = 'Bundle';
        },

        updateBundleIndex() {
            let globalBundleIndex = parseInt(localStorage.getItem('globalBundleIndex'));
            globalBundleIndex += 1;
            this.bundle.index = globalBundleIndex;
            localStorage.setItem('globalBundleIndex', globalBundleIndex.toString());
        },

        uniqueId() {
            return Math.trunc(Math.random() * 100000000);
        },

        /**
         * @deprecated
         */
        get globalBundle() {
            //console.warn('deprecated "get globalBundle" in handleGlobalBundle')
            return this.bundle;
        },
        /**
         * @deprecated
         */
        get bundleName() {
            //console.warn('deprecated "get bundleName" in handleGlobalBundle')
            return this.bundle.title;
        },
        /**
         * @deprecated
         */
        get bundleIndex() {
            //console.warn('deprecated "get bundleIndex" in handleGlobalBundle')
            return this.bundle.index;
        },
        /**
         * @deprecated
         */
        get useQuantity() {
            //console.warn('deprecated "get useQuantity" in handleGlobalBundle')
            return useQuantity;
        } ,

        /**
         * @deprecated
         */
        get shopMoneyFormat() {
            //console.warn('deprecated "get shopMoneyFormat" in handleGlobalBundle')
            return shopMoneyFormat;
        } ,
        /**
         * @deprecated
         */
        get total() {
            //console.warn('deprecated "get total" in handleGlobalBundle')
            return this.bundle.total;
        },
        /**
         * @deprecated
         */
        get totalFormatted() {
            //console.warn('deprecated "get totalFormatted" in handleGlobalBundle')
            return this.bundle.totalFormatted;
        },
    }));
    // document.addEventListener('DOMContentLoaded', function () {
    //     const sliders = document.querySelectorAll('.productitemslider');
    //     console.log('sliders are', sliders);

    //     sliders.forEach(function (sliderEl) {
    //         // Wait until the theme has mounted Splide on this element
    //         function attachWhenReady() {
    //         const splide = sliderEl.splide;
    //         if (!splide) {
    //             // Theme JS might mount it after us; try again on next frame
    //             requestAnimationFrame(attachWhenReady);
    //             return;
    //         }

    //         // ---- TRACKPAD / HORIZONTAL SCROLL ----
    //         const trackEl = sliderEl.querySelector('.splide__track') || sliderEl;

    //         let lastScrollTime = 0;
    //         const SCROLL_THROTTLE = 80; // ms between allowed moves
    //         const DELTA_THRESHOLD = .01;  // minimum horizontal scroll magnitude

    //         console.log('this scrolltrigger is added right here');

    //         trackEl.addEventListener(
    //             'wheel',
    //             function (e) {
    //                 const dx = e.deltaX || 0;
    //                 const dy = e.deltaY || 0;

    //                 // Overall gesture strength
    //                 const distance = Math.sqrt(dx * dx + dy * dy);
    //                 if (distance < DELTA_THRESHOLD) {
    //                 // Micro-movements, ignore
    //                 return;
    //                 }

    //                 const now = Date.now();
    //                 if (now - lastScrollTime < SCROLL_THROTTLE) {
    //                 // Same gesture still firing wheel events, ignore extras
    //                 return;
    //                 }

    //                 // At this point: we accept the gesture and "own" the scroll
    //                 e.preventDefault();
    //                 lastScrollTime = now;

    //                 // Decide direction: use whichever axis is stronger
    //                 let direction;
    //                 if (Math.abs(dx) > Math.abs(dy)) {
    //                 // Mostly horizontal gesture
    //                 direction = dx > 0 ? 'next' : 'prev';
    //                 } else {
    //                 // Mostly vertical gesture, but we STILL use it as slider nav
    //                 direction = dy > 0 ? 'next' : 'prev';
    //                 }

    //                 if (direction === 'next') {
    //                 splide.go('>');
    //                 } else {
    //                 splide.go('<');
    //                 }
    //             },
    //             { passive: false }
    //         );

    //         // ---- CUSTOM PREV / NEXT BUTTONS ----
    //         const wrapper = sliderEl.closest('.product-item-slider-wrapper');
    //         if (wrapper) {
    //             const prevBtn = wrapper.querySelector('.product-item-slider-prev');
    //             const nextBtn = wrapper.querySelector('.product-item-slider-next');

    //             if (prevBtn) {
    //             prevBtn.addEventListener('click', function () {
    //                 splide.go('<');
    //             });
    //             }

    //             if (nextBtn) {
    //             nextBtn.addEventListener('click', function () {
    //                 splide.go('>');
    //             });
    //             }

    //             // ---- COUNTER (1 / X) SYNC ----
    //             const currentEl = wrapper.querySelector('.slide-number');
    //             const totalEl = wrapper.querySelector('.total-slides-number');

    //             if (currentEl && totalEl) {
    //             totalEl.textContent = splide.length;

    //             splide.on('mounted move', function () {
    //                 currentEl.textContent = splide.index + 1;
    //             });
    //             }
    //         }
    //         }

    //         attachWhenReady();
    //     });
    // });
})